import React from 'react';

import {StickyPageLayout} from '@/components/Layout';
import appContent from '@/services/content/appContent.typed';
import routerService from '@/services/route/routerService';
import type {AutoDeliveryDcxStatusBadgeStatus} from '@/views/AutoDeliveryDcx/AutoDeliveryDcxStatusBadge';
import {WARNING_BANNER_SOURCE_TYPES, WarningBanners, WarningBannersTop} from '@/views/WarningBanners';

import useAutoDeliveryDcxDetails from '../hooks/useAutoDeliveryDcxDetails';
import AutoDeliveryDcxDetailsFooter from './AutoDeliveryDcxDetailsFooter';
import AutoDeliveryDcxHelpSupport from './AutoDeliveryDcxHelpSupport';
import AutoDeliveryDcxMobileNumber from './AutoDeliveryDcxMobileNumber';
import AutoDeliveryDcxNextDeliveryDetails from './AutoDeliveryDcxNextDeliveryDetails';
import AutoDeliveryDcxOrder from './AutoDeliveryDcxOrder';
import AutoDeliveryDcxOrderAddress from './AutoDeliveryDcxOrderAddress';
import AutoDeliveryDcxOrderDetails from './AutoDeliveryDcxOrderDetails';
import AutoDeliveryDcxPastOrdersButton from './AutoDeliveryDcxPastOrdersButton';
import AutoDeliveryDcxPaymentMethod from './AutoDeliveryDcxPaymentMethod';

interface AutoDeliveryDcxDetailsMobileProps {
    status: AutoDeliveryDcxStatusBadgeStatus;
    orderDetailsExtraSection?: React.ReactNode;
    showNextDeliveryHero?: boolean;
    footerActions?: React.ReactNode;
}

const AutoDeliveryDcxDetailsMobile = ({
    status,
    orderDetailsExtraSection,
    showNextDeliveryHero,
    footerActions,
}: AutoDeliveryDcxDetailsMobileProps) => {
    const {
        autoDelivery: {
            minDeliveryDaysCount,
            maxDeliveryDaysCount,
            frequency,
            isFreezed,
            status: autoDeliveryStatus,
            statusUpdateReason,
            nextDeliveryDate,
            maxNextDeliveryDate,
        },
        order: {orderId, deliveryAddress, paymentInfo, products, price, status: orderStatus},
        isOrderStatusExpected,
    } = useAutoDeliveryDcxDetails();

    const {title} = appContent.getAutoDeliveryContent()?.extendedDetailsContent || {};

    return (
        // @ts-expect-error
        <StickyPageLayout
            contentClassName='flex-1 text-start min-h-min'
            headerOptions={{
                onBack: routerService.goBack,
                title,
                endIcon: <AutoDeliveryDcxPastOrdersButton />,
            }}
            footer={
                footerActions ? <AutoDeliveryDcxDetailsFooter>{footerActions}</AutoDeliveryDcxDetailsFooter> : undefined
            }
            footerClassName='border-t-[1px] border-french-gray bg-primary-dark-5 border-t-primary-dark-15'
        >
            <WarningBannersTop source={WARNING_BANNER_SOURCE_TYPES.AUTO_DELIVERY} />
            {!showNextDeliveryHero ? (
                <AutoDeliveryDcxOrderDetails
                    className='mt-safe-offset-sticky-header p-4 pt-0'
                    status={status}
                    orderId={isOrderStatusExpected ? undefined : orderId}
                    headingAs='h2'
                    frequency={frequency}
                    minDeliveryDaysCount={minDeliveryDaysCount}
                    maxDeliveryDaysCount={maxDeliveryDaysCount}
                    extraSection={orderDetailsExtraSection}
                />
            ) : (
                <>
                    <AutoDeliveryDcxNextDeliveryDetails
                        className='mt-safe-offset-sticky-header pt-0'
                        status={autoDeliveryStatus}
                        orderStatus={orderStatus}
                        statusUpdateReason={statusUpdateReason}
                        minDeliveryDaysCount={minDeliveryDaysCount}
                        maxDeliveryDaysCount={maxDeliveryDaysCount}
                        nextDeliveryDate={nextDeliveryDate}
                        maxNextDeliveryDate={maxNextDeliveryDate}
                    />
                    <AutoDeliveryDcxOrderDetails
                        className='border-t-8 border-primary-dark-5 p-4 md:px-8'
                        headingAs='h2'
                        status={status}
                        orderId={orderId}
                        frequency={frequency}
                        minDeliveryDaysCount={minDeliveryDaysCount}
                        maxDeliveryDaysCount={maxDeliveryDaysCount}
                        extraSection={orderDetailsExtraSection}
                    />
                </>
            )}
            <AutoDeliveryDcxOrder
                className='border-t-8 border-primary-dark-5 md:px-8'
                products={products}
                price={price}
                isEditAllowed
                isUpdateDisabled={isFreezed}
                isCompact
            />
            <AutoDeliveryDcxPaymentMethod
                className='border-t-8 border-primary-dark-5 md:px-8'
                isUpdateDisabled={isFreezed}
                paymentInfo={paymentInfo}
            />
            <AutoDeliveryDcxOrderAddress
                className='border-t-8 border-primary-dark-5 md:px-8'
                address={deliveryAddress}
                isEditAllowed
                isUpdateDisabled={isFreezed}
            />
            <AutoDeliveryDcxMobileNumber className='border-t-8 border-primary-dark-5 md:px-8' />
            <AutoDeliveryDcxHelpSupport className='border-t-8 border-primary-dark-5 md:px-8' />
            {/* @ts-expect-error */}
            <WarningBanners source={WARNING_BANNER_SOURCE_TYPES.AUTO_DELIVERY} />
        </StickyPageLayout>
    );
};

export default AutoDeliveryDcxDetailsMobile;
